import { ChevronDown, Filter } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { ChannelIcon } from "@/lib/channel-icons"
import { cn } from "@/lib/utils"

// Minimal shape this filter needs — satisfied by both the calendar's
// CalendarChannel and the pipeline's Channel, so the same dropdown can be
// reused across views.
type FilterableChannel = {
  slug: string
  name: string
  channel_type: string
  color: string
}

type Props = {
  channels: FilterableChannel[]
  visible: Set<string>
  onToggle: (slug: string) => void
  onSetAll: (visible: boolean) => void
}

export function ChannelFilters({ channels, visible, onToggle, onSetAll }: Props) {
  const total = channels.length
  const shown = channels.filter((c) => visible.has(c.slug)).length
  const label =
    shown === 0
      ? "No channels"
      : shown === total
        ? "All channels"
        : `${shown} of ${total} channels`
  // Highlight the trigger whenever the list is narrowed from "all visible".
  const filtered = shown !== total

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="secondary"
          aria-label={label}
          className={cn(
            filtered &&
              "border-accent bg-accent-faded text-accent-display hover:bg-accent-faded",
          )}
        >
          <Filter className="h-4 w-4" />
          <span className="hidden sm:inline">{label}</span>
          <ChevronDown className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[14rem]">
        <DropdownMenuItem onSelect={() => onSetAll(true)}>Show all</DropdownMenuItem>
        <DropdownMenuItem onSelect={() => onSetAll(false)}>Hide all</DropdownMenuItem>
        <DropdownMenuSeparator />
        {channels.map((c) => (
          <DropdownMenuCheckboxItem
            key={c.slug}
            checked={visible.has(c.slug)}
            onSelect={() => onToggle(c.slug)}
          >
            <ChannelIcon channelType={c.channel_type} color={c.color} className="h-3.5 w-3.5" />
            <span className="truncate">{c.name}</span>
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
